import type { FoamBomLine } from './types/foamBomTypes'

function stripDiacritics(str: string): string {
  if (!str) return ''
  return str.normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/đ/g, 'd').replace(/Đ/g, 'D').toLowerCase()
}

export type FoamQuoteCatalogRule = {
  supplierId: string
  supplierName: string
  itemName: string
  mappings: Record<string, unknown>
  inputNames: string[]
  uom?: string | null
  itemId?: string | null
}

export type FoamBomAutocompleteSuggestion = {
  id: string
  name: string
  source: 'quote' | 'material' | 'bom'
  usageCount: number
  uom?: string | null
  supplierId?: string
  supplierName?: string
  mappings?: Record<string, unknown>
}

export type FoamQuoteField = 'kt1' | 'kt2' | 'kt3' | 'kt4' | 'dinh_luong'
const quoteFieldNames: Record<FoamQuoteField, string> = {
  kt1: 'KT1', kt2: 'KT2', kt3: 'KT3', kt4: 'KT4', dinh_luong: 'Định lượng',
}

const normalized = (value: unknown) => String(value ?? '').normalize('NFC').trim().replace(/\s+/g, ' ')
const keyOf = (value: unknown) => normalized(value).toLocaleLowerCase('vi')

function comparable(value: unknown): string {
  const text = normalized(value).replace(/\s+/g, '')
  const numeric = text.match(/^(-?\d+(?:[.,]\d+)?)(?:kg)?$/i)
  return numeric ? `#${Number(numeric[1]!.replace(',', '.'))}` : keyOf(text)
}

export function getFoamQuoteSemantics(itemName: string): [string, string, string, string, string] {
  const name = keyOf(itemName)
  if (name === 'xốp') return ['Dày', 'Rộng', 'Dài', '—', 'Định lượng']
  if (name === 'xốp l') return ['Dài', 'Cạnh 1', 'Cạnh 2', 'Dày', 'Định lượng']
  if (name === 'xốp góc') return ['Cạnh 1', 'Cạnh 2', 'Cạnh 3', 'Dày', 'Định lượng']
  if (name.startsWith('thùng a1') || name.startsWith('thùng a5')) return ['Dài', 'Rộng', 'Cao', '—', '—']
  if (name.startsWith('góc giấy')) return ['Cạnh 1', 'Cạnh 2', 'Cạnh 3', '—', '—']
  return ['KT1', 'KT2', 'KT3', 'KT4', 'Định lượng']
}

export function foamQuoteFieldIsUsed(itemName: string, field: FoamQuoteField, rules: FoamQuoteCatalogRule[]): boolean {
  const key = keyOf(itemName)
  const label = quoteFieldNames[field]
  return rules.some((rule) => keyOf(rule.itemName) === key && (
    rule.inputNames.includes(label) || Object.prototype.hasOwnProperty.call(rule.mappings, label)
  ))
}

type FoamQuoteRowValues = Partial<Record<FoamQuoteField, unknown>>

const FIELD_ORDER: FoamQuoteField[] = ['kt1', 'kt2', 'kt3', 'kt4', 'dinh_luong']

/** Dòng báo giá của tên hàng còn khớp các ô đã chọn. Lọc từ trên xuống: ô `skip` chỉ bị lọc bởi các ô đứng trước nó. */
function rulesMatchingChosen(itemName: string, rules: FoamQuoteCatalogRule[], row: FoamQuoteRowValues | undefined, skip?: FoamQuoteField): FoamQuoteCatalogRule[] {
  const key = keyOf(itemName)
  const limit = skip ? FIELD_ORDER.indexOf(skip) : FIELD_ORDER.length
  return rules.filter((rule) => {
    if (keyOf(rule.itemName) !== key) return false
    if (!row) return true
    for (const field of FIELD_ORDER.slice(0, limit)) {
      const chosen = row[field]
      if (chosen === null || chosen === undefined || !normalized(chosen)) continue
      const expected = rule.mappings[quoteFieldNames[field]]
      if (expected === null || expected === undefined || !normalized(expected)) continue
      if (comparable(chosen) !== comparable(expected)) return false
    }
    return true
  })
}

/** Sau khi đổi một ô: bỏ các ô phía dưới không còn khớp dòng báo giá nào (người dùng chọn lại), giữ ô còn khớp. */
export function pruneLaterFoamQuoteFields<T extends Partial<Record<FoamQuoteField, unknown>> & { item_name?: string | null }>(line: T, changed: FoamQuoteField, rules: FoamQuoteCatalogRule[]): T {
  const itemName = normalized(line.item_name)
  const start = FIELD_ORDER.indexOf(changed)
  if (!itemName || start < 0) return line
  const next = { ...line } as Record<string, unknown>
  for (const field of FIELD_ORDER.slice(start + 1)) {
    const value = next[field]
    if (value === null || value === undefined || !normalized(value)) continue
    const label = quoteFieldNames[field]
    const prefix = rulesMatchingChosen(itemName, rules, next as FoamQuoteRowValues, field)
    const stillValid = prefix.some((rule) => {
      const expected = rule.mappings[label]
      return expected === null || expected === undefined || !normalized(expected) || comparable(expected) === comparable(value)
    })
    if (!stillValid) next[field] = null
  }
  return next as T
}

/** Giá trị còn khả thi cho một ô, lọc theo các ô đã chọn (lọc nhiều tầng). */
export function foamQuoteSuggestions(itemName: string, field: FoamQuoteField, rules: FoamQuoteCatalogRule[], row?: FoamQuoteRowValues): string[] {
  const label = quoteFieldNames[field]
  const values = new Set<string>()
  for (const rule of rulesMatchingChosen(itemName, rules, row, field)) {
    const mapping = rule.mappings[label]
    if (mapping !== null && mapping !== undefined && normalized(mapping)) values.add(normalized(mapping))
  }
  return [...values].sort((a, b) => a.localeCompare(b, 'vi', { numeric: true }))
}

/** Ô chọn chỉ còn đúng một giá trị hợp lệ thì tự điền (lặp đến khi hết); ô nhập lúc mua (không có giá trị trong báo giá) giữ nguyên. */
export function autoFillSingleFoamQuoteFields<T extends Partial<Record<FoamQuoteField, unknown>> & { item_name?: string | null }>(line: T, rules: FoamQuoteCatalogRule[]): T {
  const itemName = normalized(line.item_name)
  if (!itemName) return line
  const next = { ...line } as Record<string, unknown>
  const order: FoamQuoteField[] = ['kt1', 'kt2', 'kt3', 'kt4', 'dinh_luong']
  for (let pass = 0; pass < order.length; pass++) {
    let changed = false
    for (const field of order) {
      const current = next[field]
      if (current !== null && current !== undefined && normalized(current)) continue
      const options = foamQuoteSuggestions(itemName, field, rules, next as FoamQuoteRowValues)
      if (options.length !== 1) continue
      next[field] = field === 'kt4' || field === 'dinh_luong' ? options[0] : Number(options[0].replace(',', '.'))
      if (typeof next[field] === 'number' && Number.isNaN(next[field])) next[field] = options[0]
      changed = true
    }
    if (!changed) break
  }
  return next as T
}

export function validateFoamQuoteRow(
  row: Pick<FoamBomLine, 'item_name' | 'kt1' | 'kt2' | 'kt3' | 'kt4' | 'dinh_luong'>,
  rules: FoamQuoteCatalogRule[],
): { valid: boolean; message: string; matchingRuleCount: number } {
  const itemName = normalized(row.item_name)
  if (!itemName) return { valid: false, message: 'Thiếu tên hàng', matchingRuleCount: 0 }
  const semantics = getFoamQuoteSemantics(itemName)
  const values: Record<string, unknown> = {
    KT1: row.kt1, KT2: row.kt2, KT3: row.kt3, KT4: row.kt4, 'Định lượng': row.dinh_luong,
  }
  for (const [index, field] of ['KT1', 'KT2', 'KT3', 'KT4', 'Định lượng'].entries()) {
    if (semantics[index] !== '—' && !normalized(values[field])) {
      return { valid: false, message: `Thiếu ${field}`, matchingRuleCount: 0 }
    }
  }

  const candidates = rules.filter((rule) => normalized(rule.itemName) === itemName)
  const matches = candidates.filter((rule) => {
    for (const [field, expected] of Object.entries(rule.mappings)) {
      if (!['KT1', 'KT2', 'KT3', 'KT4', 'Định lượng'].includes(field) || expected === '' || expected == null) continue
      if (comparable(values[field]) !== comparable(expected)) return false
    }
    return true
  })
  return matches.length
    ? { valid: true, message: '', matchingRuleCount: matches.length }
    : { valid: false, message: 'Không có trong báo giá', matchingRuleCount: 0 }
}

export function foamQuoteItemNames(rules: FoamQuoteCatalogRule[]): string[] {
  const names = new Map<string, string>()
  for (const rule of rules) {
    const name = normalized(rule.itemName)
    const key = keyOf(name)
    if (key && !names.has(key)) names.set(key, name)
  }
  return [...names.values()].sort((a, b) => a.localeCompare(b, 'vi', { sensitivity: 'base' }))
}

function autocompleteKey(value: unknown): string {
  return stripDiacritics(normalized(value)).replace(/\s+/g, ' ')
}

/**
 * Build the packaging BOM name picker from already-authorized read sources.
 * Prefix matches rank first; repeated use is the second tie-breaker.
 */
export function buildFoamBomAutocompleteSuggestions(input: {
  query: string
  quoteRules: FoamQuoteCatalogRule[]
  materials: Array<{ id: string; name: string; uom_default?: string | null; group_code?: string | null; status?: string }>
  standardNames: Array<{ id: string; standard_name: string; default_uom?: string | null; bom_usage_count?: number }>
  usedNames?: string[]
  limit?: number
}): FoamBomAutocompleteSuggestion[] {
  const byName = new Map<string, FoamBomAutocompleteSuggestion>()
  const add = (item: FoamBomAutocompleteSuggestion) => {
    const key = autocompleteKey(item.name)
    if (!key) return
    const existing = byName.get(key)
    if (!existing || (item.source === 'quote' && existing.source !== 'quote') || (item.source === existing.source && item.usageCount > existing.usageCount)) {
      byName.set(key, item)
    }
  }

  const usedCounts = new Map<string, number>()
  for (const name of input.usedNames ?? []) {
    const key = autocompleteKey(name)
    if (key) usedCounts.set(key, (usedCounts.get(key) ?? 0) + 1)
  }

  for (const rule of input.quoteRules) {
    add({
      id: `quote:${rule.supplierId}:${rule.itemName}`,
      name: normalized(rule.itemName),
      source: 'quote',
      usageCount: (usedCounts.get(autocompleteKey(rule.itemName)) ?? 0) + 1,
      uom: rule.uom,
      supplierId: rule.supplierId,
      supplierName: rule.supplierName,
      mappings: rule.mappings,
    })
  }

  for (const material of input.materials) {
    if (material.status === 'inactive' || !['bb', 'nk'].includes(material.group_code ?? '')) continue
    add({
      id: `material:${material.id}`,
      name: normalized(material.name),
      source: 'material',
      usageCount: usedCounts.get(autocompleteKey(material.name)) ?? 0,
      uom: material.uom_default,
    })
  }

  for (const standard of input.standardNames) {
    add({
      id: `bom:${standard.id}`,
      name: normalized(standard.standard_name),
      source: 'bom',
      usageCount: (standard.bom_usage_count ?? 0) + (usedCounts.get(autocompleteKey(standard.standard_name)) ?? 0),
      uom: standard.default_uom,
    })
  }

  const query = autocompleteKey(input.query)
  return [...byName.values()]
    .filter((item) => !query || autocompleteKey(item.name).includes(query))
    .sort((a, b) => {
      const aKey = autocompleteKey(a.name)
      const bKey = autocompleteKey(b.name)
      const aPrefix = query && aKey.startsWith(query) ? 1 : 0
      const bPrefix = query && bKey.startsWith(query) ? 1 : 0
      return bPrefix - aPrefix || b.usageCount - a.usageCount || a.name.localeCompare(b.name, 'vi', { sensitivity: 'base' })
    })
    .slice(0, input.limit ?? 30)
}

/** Tên hàng dùng để tra giá. Dòng đã lưu chỉ có item_name (không có loai), dòng mới gõ có cả hai. */
export function foamBomPriceKind(line: { item_name?: string | null; loai?: string | null }): string | null {
  const name = String(line.item_name ?? '').trim() || String(line.loai ?? '').trim()
  return name || null
}
